import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import CamUp from './CamUp'; 
import { onSnapshot, collection, doc, getDoc, getDocs, setDoc, query, limit, FieldValue, increment, updateDoc, where } from 'firebase/firestore';
import { signInWithPopup, onAuthStateChanged, signOut, signInWithEmailAndPassword, getIdToken } from "firebase/auth";
import { db, auth, provider } from './firebase';
import { vote, getVoteCount } from './vote';
import SongCard from './SongCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import './App.css';
import DailyVoteGoalProgressBar from './DailyVoteGoalProgressBar';
import VotesToGoLiveProgressBar from './VotesToGoLiveProgressBar';
import OnboardingModal from './OnboardingCard';
import Leaderboard from './LeaderBoard';
import UserProfile from './UserProfile';
import UnrankedEarStatus from './UnrankedEarStatus';
import VotePage from './VotePage';
import Player from './Player';
import PlayerCard from './PlayerCard';

function App() {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';
  const [user, setUser] = useState(null);
  const [userVoteCount, setUserVoteCount] = useState(0);
  const [dailyVoteCount, setDailyVoteCount] = useState(0);
  const [earStatus, setEarStatus] = useState(0);
  const [audioFiles, setAudioFiles] = useState([]);
  const [fileBuffer, setFileBuffer] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const progressPercentage = (dailyVoteCount / 5) * 100;
  const [playlistProgressPercentage, setPlaylistProgressPercentage] = useState(0);
  const [votesToGoLive, setVotesToGoLive] = useState(0);
  const [totalVotes, setTotalVotes] = useState(0);
  const [votesToGoLivePercentage, setVotesToGoLivePercentage] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tiktokUsername, setTiktokUsername] = useState('');
  const [isLoginForm, setIsLoginForm] = useState(true);
  const [activeScreen, setActiveScreen] = useState('vote');
  const [playingSong, setPlayingSong] = useState(null);
  const node = useRef();
  const [errorMessage, setErrorMessage] = useState('');
  const [sessionProgressPercentage, setSessionProgressPercentage] = useState(0);
  const [matchedUpvotes, setMatchedUpvotes] = useState(0);
  const [mismatchedUpvotes, setMismatchedUpvotes] = useState(0);
  // const [credit, setCredit] = useState(null);
  const [totalSongs, setTotalSongs] = useState(0);
  const [playedOrVotedCount, setPlayedOrVotedCount] = useState(0);
  const [hasOnBoarded, setHasOnBoarded] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearched, setIsSearched] = useState(false);
  let earStatusRank;

  function PrivateRoute({ children, ...rest }) {
    

    return (
      user ? children : <Navigate to="/vote" replace />
    );
  }

  // handle click outside hook
  useEffect(() => {
    const handleClickOutside = e => {
      if (node.current.contains(e.target)) {
        return;
      }
      setMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // handle sign in
  const handleSignIn = async (e) => {
    e.preventDefault();
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      if (user) {  
        // The user is now signed in, get the ID token
        const idToken = await getIdToken(user);
  
        // Save uid and idToken to local storage or use it in your app
        localStorage.setItem('uid', user.uid);
        localStorage.setItem('idToken', idToken);
        setMenuOpen(false);
      } else {
        console.error('Error signing in');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  //handle sign up
  const handleSignUp = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${SERVER_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, firstName, lastName, tiktokUsername })
      });
  
      const data = await response.json();
  
      if (data.uid) {
        // Use this uid to log in the user on the client side
        // console.log(`User created with ID: ${data.uid}`);
        handleSignIn(e); // Call the sign in function after successful sign up
      } else {
        // Something went wrong
        setErrorMessage(data.error);
        console.log('Error signing up', data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const signInWithGoogle = async () => {
    signInWithPopup(auth, provider)
      .then(async (result) => {
        setUser(result.user);
        setMenuOpen(false);
  
        const userDocRef = doc(db, 'users', result.user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          // The document does not exist, handle it accordingly.
          // For example, you can create the document here.
          // await setDoc(doc(db, 'users', result.user.uid), {
            // name: result.user.displayName,
            // email: result.user.email,
            // voteCount: 0,
            // hasOnBoarded: false,
            // // credits: 0,
            // earStatus: 0,
            // phoneNumber: result.user.phoneNumber,
            // photoUrl: result.user.photoURL,
            // tiktokUsername: null,
            // sessionsVotedIn: 0,
            // streakCount: 0,
            // matchedUpvotes: 0,
            // mismatchedUpvotes: 0,
            // matchedDownvotes: 0,
            // mismatchedDownvotes: 0,
            // votes: {},
            // userTypes: []
          // });
        }
      })
      .catch((error) => {
        console.error("Error signing in with Google: ", error);
      });
  };

  // sign out
  const signOutUser = () => {
    signOut(auth)
      .then(() => {
        setUser(null);
      })
      .catch((error) => {
        console.error("Error signing out: ", error);
      });
  };

  // Fetch global votes to go live value
  useEffect(() => {
    const fetchVotesToGoLive = async () => {
      const globalDocRef = doc(db, 'global', 'votes');
      const globalDocSnap = await getDoc(globalDocRef);

      try {
        const globalDocSnap = await getDoc(globalDocRef);
  
        if (globalDocSnap.exists()) {
          setVotesToGoLive(globalDocSnap.data().votesToGoLive || 0);
        }
      } catch (error) {
        console.error("Error fetching global votes to go live:", error);
        // Handle or display the error appropriately
        // You can set a state variable here to inform the user about the error
      }
    };
  
    fetchVotesToGoLive();
  }, [db]);

  // Set up listener for total vote count
  useEffect(() => {
    const countersCollectionRef = collection(db, 'counters');

    const unsubscribe = onSnapshot(countersCollectionRef, (querySnapshot) => {
      let totalVotes = 0;

      querySnapshot.forEach((doc) => {
        totalVotes += doc.data().count;
      });

      setTotalVotes(totalVotes);
    });

    return () => unsubscribe();
  }, [db]);

  // Calculate votes to go live percentage
  useEffect(() => {
    const percentage = votesToGoLive !== 0 ? (totalVotes / votesToGoLive) * 100 : 0;
    setVotesToGoLivePercentage(Math.min(100, Math.max(0, percentage)));
  }, [totalVotes, votesToGoLive]);

  // fetch files 
  const fetchFiles = async (searchQuery = null) => {
    setIsLoading(true);
    const fileCollection = collection(db, "files_VoteMeIn");
    const fileSnapshot = await getDocs(fileCollection);

    let files = await Promise.all(
      fileSnapshot.docs.map(async docData => {
        const filePath = docData.data().files;

        if (typeof filePath === 'string') {
          const [collection, docId] = filePath.split('/'); // Split the path into collection and document ID
          const fileRef = doc(db, collection, docId); // Construct the DocumentReference
          const fileSnap = await getDoc(fileRef);
          return { ...fileSnap.data(), id: fileSnap.id };
        } else if (typeof filePath === 'object') {
          const collection = filePath.parent.path; // should return 'files'
          const docId = filePath.id;
          const fileRef = doc(db, collection, docId); // Construct the DocumentReference
          const fileSnap = await getDoc(fileRef);
          return { ...fileSnap.data(), id: fileSnap.id };
        } else {
          // Handle the case when filePath is not a string
          console.log('filePath is not a string or is undefined:', filePath);
          return null;
        }
      })
    );

    // Search files
    if (searchQuery) {
      setIsSearched(true);
      if (files.length === 0) {
        // No results found after a search
        setIsLoading(false); // you might want to ensure the loading state is set to false here
        return; // Optionally early return to skip further processing if desired
      }
      const searchLowerCase = searchQuery.toLowerCase();  // Convert the search query to lowercase
        
        files = files.filter(file => 
            (file.name && file.name.toLowerCase().includes(searchLowerCase)) ||  // Check the name
            (file.folderName && file.folderName.toLowerCase().includes(searchLowerCase))  // Check the folderName
        );
    }

    let playedOrVotedCount = 0;

    // remove played songs from queue and add exp
    if (user) {
      // Get user document
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      // Filter out files that user has played or voted on
      files = files.filter(file => {
        // Check if the file is in the user's playedSongs array
        const played = userData.playedSongs.includes(file.id);

        // Check if the user has voted on this song
        const voted = userData.votes && Object.keys(userData.votes).includes(file.id);
        // If the user has played or voted, increment the counter
        if (played || voted) {
          playedOrVotedCount++;
        }
        return !(played || voted);
      });
    }

    const totalFiles = fileSnapshot.size;
    setPlaylistProgressPercentage((playedOrVotedCount / totalFiles) * 100);

    files = shuffle(files); // Shuffle the files after fetching them
    setAudioFiles(files);
    setFileBuffer(files);
    setIsLoading(false); // Set loading state to false
  }  

  useEffect(() => {
    if(user) {
      fetchFiles();
    }
  }, [user]);
    
  // Fisher-Yates (aka Knuth) Shuffle algorithm function
  function shuffle(array) {
    let currentIndex = array.length, temporaryValue, randomIndex;
  
    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
  
      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
  
    return array;
  }  

  // load random files
  const loadRandomFiles = () => {
    // setAudioFiles([]); // Clear the current list of audio files

    if (fileBuffer.length < 5) {
      fetchFiles();
      return;
    }

    let newBuffer = [...fileBuffer];
    let newFiles = [];

    for (let i = 0; i < 5; i++) {
      const randomIndex = Math.floor(Math.random() * newBuffer.length);
      newFiles.push(newBuffer[randomIndex]);
      newBuffer = newBuffer.filter((_, index) => index !== randomIndex);
    }
    
    setAudioFiles(newFiles);
    setFileBuffer(newBuffer);
};

  useEffect(() => {
    if (fileBuffer.length >= 5) {
      loadRandomFiles();
    }
  }, [fileBuffer]);

  // unsubscribe user
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });

    return () => {
      unsubscribeAuth();
    }
  }, []);
  
  // subscribe user
  useEffect(() => {
    if (user) {
      const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
        const data = snapshot.data();
        if (data) {
          setUserVoteCount(data.voteCount || 0);
          setEarStatus(data.earStatus || 0); // default to 0 if no data found
          setHasOnBoarded(data.hasOnBoarded || false); // default to false if no data found
          setDailyVoteCount(data.dailyVoteCount || 0);
          const sessionsVotedIn = data.sessionsVotedIn || 0;
          const progress = convertSessionsToPercentage(sessionsVotedIn);
          setSessionProgressPercentage(progress);
          // setCredit(data.credits || 0)
        } else {
          setUserVoteCount(0);
          setEarStatus(0);
          setHasOnBoarded(false);
          setDailyVoteCount(0);
          setSessionProgressPercentage(0);
        }
      });
  
      return () => unsubscribe();
    }
  }, [user, db]);

  // handle vote
  const handleVote = async (fileId, type) => {
    if (!user) {
      setMenuOpen(true);  // open the menu
      return;
    }

    // Fetch user vote count for the day
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    const userVoteCount = docSnap.data().voteCount || 0;

    if (type === 'upvote') {
      // if (userVoteCount >= 50) {
      //   // Maximum number of votes reached
      //   console.log('You have reached your daily voting limit');
      //   return;
      // }
      if (userVoteCount < 0) {
        // No votes to decrement
        console.log('Your vote count cannot go below 0');
        return;
      }
    }

    // earStatusRank = (docSnap.data().sessionsVotedIn / 10) * 100;
    await vote(db, user.uid, fileId, type);
    // Update daily vote count
    await updateDoc(docRef, { dailyVoteCount: increment(1) });
    // After vote is cast, fetch updated data for the file
    const fileRef = doc(db, "files", fileId);
    const fileSnapshot = await getDoc(fileRef);
    const fileData = { ...fileSnapshot.data(), id: fileSnapshot.id };

    // Update the state with the updated file data
    setAudioFiles((prevFiles) => {
      return prevFiles.map((file) => file.id === fileId ? fileData : file);
    });
  }

  // render audio files
  const renderAudioFiles = () => (
    audioFiles
      .filter(file => 
        file && 
        file.name && 
        file.sessionType &&
        file.name.toLowerCase().includes(searchTerm.toLowerCase()) // Search logic
      )
      .map((file) => (
        <SongCard key={file.id} file={file} handleVote={handleVote} user={user} playingSong={playingSong} setPlayingSong={setPlayingSong} />
      ))
  );
  
  // tik tok agent recognition -- displays message for users accessing in tik tok
  const [includesByteDanceText, setIncludesByteDanceText] = useState(false);
  useEffect(() => {
    const userAgent = navigator.userAgent;
    if (/BytedanceWebview/i.test(userAgent)) {
      setIncludesByteDanceText(true);
    }
  }, []);

  function convertSessionsToPercentage(sessionsVotedIn) {
    return Math.min(sessionsVotedIn * 10, 100);
  }

  const handleSearchCancel = () => {
    setSearchTerm('');
    fetchFiles();
  };

  return (
    <div className="App">
      <Router>
      {includesByteDanceText && (
        <div style={{color: 'red', margin: '21px 63px'}} className="alert">
          For the best experience, please open in your regular browser.
        </div>
      )}
      {/* <div className="header header-image">
        <div className='credits'>
          <img width="14" height="14" src="https://img.icons8.com/arcade/64/coins--v1.png" alt="coins--v1"/>
          {credit}
        </div>
        
        <button className='menu-button' onClick={() => setMenuOpen(true)}>
          <svg width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 10.6667H24M8 16.0001H24M8 21.3334H24" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div className='header-logo'>
          <img src='https://uploads-ssl.webflow.com/641b56cbb2396a6318364e55/644991a8c2c53942bd0416d6_Timbo-Idol_Primary%20Mark.png' />
        </div>
      </div> */}

      <div ref={node} className={`fullscreen-menu ${menuOpen ? 'open' : ''}`}>
        {menuOpen && (
          <div style={{display: 'flex', flexDirection: 'column'}}>
            {user ? (
            <div className='header-logged-in'>
              <h1 className='menu-header-welcome'>{user.displayName}</h1>
              <hr/>
              <nav>
                {/* <Link to="/vote" className='nav-vote'>Vote</Link>
                <Link to="/leaderboard" className='nav-leaderboard'>Leaderboard</Link> */}
                <Link to="/user-profile" className='nav-leaderboard'>Profile</Link>
              </nav>
              <button className='sign-out-button' onClick={signOutUser}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.3335 16.0001C5.3335 16.3537 5.47397 16.6928 5.72402 16.9429C5.97407 17.1929 6.31321 17.3334 6.66683 17.3334H16.7868L13.7202 20.3867C13.5952 20.5107 13.496 20.6582 13.4283 20.8206C13.3606 20.9831 13.3258 21.1574 13.3258 21.3334C13.3258 21.5094 13.3606 21.6837 13.4283 21.8462C13.496 22.0087 13.5952 22.1561 13.7202 22.2801C13.8441 22.4051 13.9916 22.5042 14.1541 22.5719C14.3165 22.6396 14.4908 22.6745 14.6668 22.6745C14.8428 22.6745 15.0171 22.6396 15.1796 22.5719C15.3421 22.5042 15.4895 22.4051 15.6135 22.2801L20.9468 16.9467C21.0682 16.8199 21.1634 16.6704 21.2268 16.5067C21.3602 16.1821 21.3602 15.818 21.2268 15.4934C21.1634 15.3297 21.0682 15.1802 20.9468 15.0534L15.6135 9.72008C15.4892 9.59576 15.3416 9.49715 15.1792 9.42987C15.0167 9.36259 14.8426 9.32796 14.6668 9.32796C14.491 9.32796 14.3169 9.36259 14.1545 9.42987C13.9921 9.49715 13.8445 9.59576 13.7202 9.72008C13.5958 9.8444 13.4972 9.99199 13.4299 10.1544C13.3627 10.3168 13.328 10.4909 13.328 10.6667C13.328 10.8426 13.3627 11.0167 13.4299 11.1791C13.4972 11.3415 13.5958 11.4891 13.7202 11.6134L16.7868 14.6667H6.66683C6.31321 14.6667 5.97407 14.8072 5.72402 15.0573C5.47397 15.3073 5.3335 15.6465 5.3335 16.0001ZM22.6668 2.66675H9.3335C8.27263 2.66675 7.25521 3.08818 6.50507 3.83832C5.75492 4.58847 5.3335 5.60588 5.3335 6.66675V10.6667C5.3335 11.0204 5.47397 11.3595 5.72402 11.6096C5.97407 11.8596 6.31321 12.0001 6.66683 12.0001C7.02045 12.0001 7.35959 11.8596 7.60964 11.6096C7.85969 11.3595 8.00016 11.0204 8.00016 10.6667V6.66675C8.00016 6.31313 8.14064 5.97399 8.39069 5.72394C8.64074 5.47389 8.97987 5.33341 9.3335 5.33341H22.6668C23.0205 5.33341 23.3596 5.47389 23.6096 5.72394C23.8597 5.97399 24.0002 6.31313 24.0002 6.66675V25.3334C24.0002 25.687 23.8597 26.0262 23.6096 26.2762C23.3596 26.5263 23.0205 26.6667 22.6668 26.6667H9.3335C8.97987 26.6667 8.64074 26.5263 8.39069 26.2762C8.14064 26.0262 8.00016 25.687 8.00016 25.3334V21.3334C8.00016 20.9798 7.85969 20.6407 7.60964 20.3906C7.35959 20.1406 7.02045 20.0001 6.66683 20.0001C6.31321 20.0001 5.97407 20.1406 5.72402 20.3906C5.47397 20.6407 5.3335 20.9798 5.3335 21.3334V25.3334C5.3335 26.3943 5.75492 27.4117 6.50507 28.1618C7.25521 28.912 8.27263 29.3334 9.3335 29.3334H22.6668C23.7277 29.3334 24.7451 28.912 25.4953 28.1618C26.2454 27.4117 26.6668 26.3943 26.6668 25.3334V6.66675C26.6668 5.60588 26.2454 4.58847 25.4953 3.83832C24.7451 3.08818 23.7277 2.66675 22.6668 2.66675Z" fill="black"/>
                </svg>        
                <span style={{paddingLeft: "11px", color: 'black'}}>Logout</span>
              </button>
              <p style={{fontSize: '12px', marginTop: '138px'}}><a style={{color: '#0083bd'}} href = "mailto: admin@lightenergylabs.xyz">Email Support</a></p>
            </div>
            ) : (
              <div className='header-logged-out'>
                {isLoginForm ? (
                  <>
                    <h2>Timbaland Needs <br/> Your Help!</h2>
                    <p>Sign up, vote now, and shape the future of music.</p>
                    <div className='auth-buttons'>
                      <button className='login-button google' onClick={signInWithGoogle}></button>
                      <button className='login-button tiktok'></button>
                      <button className='login-button apple'></button>
                    </div> 
                    <form onSubmit={handleSignIn}>
                      <div className='sign-in-input'>
                        <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
                        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
                      </div>
                      <button className='login-button' type="submit">Login</button>
                      <button className='sign-up-button' onClick={() => setIsLoginForm(false)}>Sign Up</button>
                    </form>
                    <p style={{fontSize: '12px', marginTop: '138px', color: '#0083bd'}}><a style={{color: '#0083bd'}} href = "mailto: admin@lightenergylabs.xyz">Email Support</a></p>
                  </>
                ) : ( 
                  <>
                    <h1 style={{marginTop: '0'}} className='menu-header'>Timbaland needs your help finding music’s next stars.</h1>
                    <p>Sign Up With</p>
                    <form onSubmit={handleSignUp}>
                    <div className='sign-up-buttons'>
                      <button className='sign-in-button google' onClick={signInWithGoogle}></button>
                      <button className='sign-in-button tiktok'></button>
                      <button className='sign-in-button apple'></button>
                    </div> 
                    <div className='sign-up-name'>
                      <input className='firstname' name="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First Name" />
                      <input className='lastname' name="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last Name" />
                    </div>
                    <div className='sign-up-input'>
                      <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
                      <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
                      <input name="tiktokUsername" value={tiktokUsername} onChange={(e) => setTiktokUsername(e.target.value)} placeholder="TikTok Username (optional)" />
                    </div>
                    {errorMessage && <p className="error-message">{errorMessage}</p>}

                    <button className='sign-up-button' type="submit">Create An Account</button>
                    <br/>
                    <button style={{background: 'none', border: 'none', color: 'black'}} onClick={() => setIsLoginForm(true)}>Back</button>
                  </form>
                  <p style={{fontSize: '12px', color: '#0083bd'}}><a style={{color: '#0083bd'}} href = "mailto: admin@lightenergylabs.xyz">Email Support</a></p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {/* <button className="close-button" onClick={() => setMenuOpen(false)}>
        </button> */}
      </div>
      <div className='main'>
      <Routes>
      <Route path="/" element={
        <>
          <div className='cam-up-logo'></div>
          <p>Coming Soon</p>
        </>
      } />
        <Route path="/timbaland/upload" element={<UserProfile user={user} votesToGoLivePercentage={votesToGoLivePercentage} sessionProgressPercentage={sessionProgressPercentage} playlistProgress={playlistProgressPercentage} />} />
      </Routes>
      </div>
    </Router>
    </div>
  );
}

export default App;
